import React, {useState} from 'react';
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/PageHeader";
import Pagination from "../../../components/Pagination";
import {EyeIcon, FunnelIcon, CreditCardIcon} from "@heroicons/react/24/outline";
import StatusBadge from "../../../components/StatusBadge";
import {useTranslation} from "react-i18next";
import {useMutation, useQuery} from "react-query";
import {Link} from "react-router-dom";
import SortIcon from "../../../components/SortIcon";
import SelectInput from "../../../components/SelectInput";
import Loading from "../../../components/Loading";
import {apollo} from "../../../utils/apollo";
import {
    GET_MERCHANT_SUPPLIERS_LIST,
    GET_SETTLEMENT_LIST,
    GET_SETTLEMENT_REPORT_URL,
    REQUEST_SETTLEMENT
} from "../../../utils/graphql";
import IconButton from "../../../components/IconButton";
import ButtonInternalLink from "../../../components/ButtonInternalLink";
import PaginationFull from "../../../components/PaginationFull";
import Button from "../../../components/Button";
import {toast} from "react-toastify";
import {clearCachedKey} from "../../../utils/helpers";
import useModalStore from "../../../store/modal";

function Settlement() {
    const {t} = useTranslation();
    const {setModal} = useModalStore();
    const [sort, setSort] = useState({field: 'id', isAscending: false});
    const [supplier, setSupplier] = useState(null);
    const [page, setPage] = useState(1);
    const [showFilter, setShowFilter] = useState(false);
    const [statusFilter, setStatusFilter] = useState('')
    // const {data: records, isLoading, error} = useQuery('payment_links', getPaymentLinks)

    const tableHeader = [
        {column: 'settlement_uuid', title: t('settlement.table.settlement_uuid'), sortable: true},
        // {column: 'merchant_id', title: t('settlement.table.merchant_id'), sortable: false},
        // {column: 'user_id', title: t('settlement.table.user_id'), sortable: false},
        // {column: 'authority_id', title: t('settlement.table.authority_id'), sortable: false},
        {column: 'supplier', title: t('settlement.table.supplier'), sortable: false},
        {column: 'request_amount', title: t('settlement.table.request_amount'), sortable: true},
        {column: 'settlement_amount', title: t('settlement.table.settlement_amount'), sortable: true},
        {column: 'settlement_date', title: t('settlement.table.settlement_date'), sortable: true},
        {column: 'status', title: t('settlement.table.status'), sortable: true},
        {column: 'receipt', title: t('settlement.table.receipt'), sortable: false},
        {column: 'report', title: t('settlement.table.report'), sortable: false},
        {column: 'paymentReference', title: t('settlement.table.paymentReference'), sortable: false},
        // {column: 'created_at', title: t('settlement.table.created_at'), sortable: true},
        {column: 'actions', title: t('settlement.table.actions'), sortable: false, hidden: true},
    ]

    const requestSettlementMutation = useMutation({
        mutationFn: async () => {
            const {data} = await apollo.mutate({
                mutation: REQUEST_SETTLEMENT,
                variables: {
                    supplier: supplier,
                },
            });
            return data?.requestSettlement;
        },
        onSuccess: (data) => {
            if (data && data.success) {
                toast.success(data.message);
            } else {
                toast.error(data.message)
            }
            clearCachedKey(['settlement_list'])
        },
    })

    const getSettlementReportUrlMutation = useMutation({
        mutationFn: async (variables) => {
            const {data} = await apollo.mutate({
                mutation: GET_SETTLEMENT_REPORT_URL,
                variables: {
                    settlementUuid: variables?.settlementUuid
                },
            });
            if (!!data?.getSettlementReportUrl) {
                window.location.href = data?.getSettlementReportUrl;
            }
        }
    })

    const fetchSettlements = useQuery(
        ['settlement_list', {page, sort, statusFilter}],
        async () => {
            const {data} = await apollo.query({
                query: GET_SETTLEMENT_LIST,
                variables: {
                    sortBy: sort.field?.toUpperCase(),
                    sortDirection: sort.isAscending ? "ASC" : "DESC",
                    page,
                    first: 15,
                    filter: {
                        status: statusFilter
                    }
                },
            });
            return data?.getSettlementList;
        },
        {keepPreviousData: false}
    );
    const fetchMerchantSuppliersList = useQuery({
        queryKey: 'merchant_suppliers_list',
        queryFn: async () => {
            const {data} = await apollo.query({
                query: GET_MERCHANT_SUPPLIERS_LIST,
                variables: {},
            });
            return data?.getMerchantSuppliersList;
        },
        keepPreviousData: false
    });

    const records = fetchSettlements?.data || null;
    const suppliersList = fetchMerchantSuppliersList?.isSuccess ? fetchMerchantSuppliersList?.data : null;

    const handleChangeSort = (field) => {
        if (sort.field === field) {
            setSort(prevState => ({...prevState, isAscending: !prevState.isAscending}));
        } else {
            setSort({field: field, isAscending: true});
        }
    };

    const handleRequestSettlement = () => {
        if (requestSettlementMutation.isLoading) return;

        setModal({
            isVisible: true,
            title: t('settlement.confirm_settlement_title'),
            description: t('settlement.confirm_settlement_description'),
            confirmText: t('settlement.confirm_settlement_button'),
            onConfirm: () => {
                requestSettlementMutation.mutate();
            }
        });
    }

    return (
        <DashboardLayout>
            <div>
                <div className="mb-6">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg bg-gradient-to-br from-green-500 to-blue-500 shadow-sm">
                            <CreditCardIcon className="h-6 w-6 text-white" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900">{t('settlement.page_title')}</h2>
                    </div>
                    <p className="text-gray-600 mt-1">{t('settlement.page_description')}</p>
                </div>

                <div className="flex justify-end mb-6">
                    <Button
                        disabled={requestSettlementMutation.isLoading}
                        onClick={handleRequestSettlement}
                    >
                        {t('settlement.request_settlement')}
                    </Button>
                </div>

                <div className="bg-white rounded-xl shadow-md border border-gray-200 mb-8">
                    <div className="px-6 py-4 bg-gradient-to-br from-green-500 to-blue-500 border-b border-gray-200 rounded-t-xl">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="p-2 rounded-lg bg-white/20">
                                    <FunnelIcon className="h-5 w-5 text-white" />
                                </div>
                                <h3 className="text-lg font-medium text-white">{t('settlement.filter_options')}</h3>
                            </div>
                        </div>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <SelectInput 
                                    name="supplier-filter" 
                                    label={t('settlement.filter.supplier')}
                                    id="supplier-filter" 
                                    get={supplier}
                                    set={v => setSupplier(v)}
                                    className="w-full"
                                >
                                    <option value="">Merchant</option>
                                    {suppliersList?.map(supplier => <option key={supplier.supplierUuid} value={supplier.supplierUuid}>Vendor: {supplier.supplierName}</option>)}
                                </SelectInput>
                            </div>
                            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                <SelectInput 
                                    name="status-filter" 
                                    label={t('settlement.filter.filter_by_status')}
                                    id="status-filter" 
                                    get={statusFilter}
                                    set={setStatusFilter}
                                    className="w-full"
                                >
                                    <option value="">All</option>
                                    <option value="pending">Pending</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                    <option value="under process">Under Process</option>
                                    <option value="completed">Completed</option>
                                </SelectInput>
                            </div>
                        </div>
                    </div>
                </div>

                {fetchSettlements.isLoading && (
                    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
                        <div className="flex justify-center items-center">
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <div className="h-16 w-16 rounded-full bg-gradient-to-br from-green-500 to-blue-500 opacity-20 animate-ping"></div>
                                </div>
                                <div className="relative flex items-center justify-center">
                                    <div className="h-16 w-16 rounded-full bg-gradient-to-br from-green-500 to-blue-500 flex items-center justify-center">
                                        <CreditCardIcon className="h-8 w-8 text-white" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <h3 className="mt-6 text-lg font-semibold text-gray-900">{t('settlement.loading_title') || 'Loading Settlements'}</h3>
                        <p className="mt-2 text-sm text-gray-500">{t('settlement.loading_description') || 'Please wait while we fetch your settlement data...'}</p>
                    </div>
                )}

                {(fetchSettlements.isSuccess && (!records || !records?.data?.length)) && (
                    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-12 text-center">
                        <div className="mx-auto h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center">
                            <CreditCardIcon className="h-8 w-8 text-gray-400" />
                        </div>
                        <h3 className="mt-6 text-lg font-semibold text-gray-900">{t('settlement.no_data_title') || 'No Settlements Found'}</h3>
                        <p className="mt-2 text-sm text-gray-500">{t('settlement.no_data_description') || 'You have not created any settlements yet.'}</p>
                        <div className="mt-6">
                            <Button
                                onClick={() => fetchSettlements.refetch()} 
                            >
                                {t('settlement.refresh_data') || 'Refresh Data'}
                            </Button>
                        </div>
                    </div>
                )}

                {(fetchSettlements.isSuccess && records && records?.data?.length > 0) && (
                    <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
                        <div className="px-6 py-4 bg-gradient-to-br from-green-500 to-blue-500 border-b border-gray-200 rounded-t-xl">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <div className="p-2 rounded-lg bg-white/20">
                                        <CreditCardIcon className="h-5 w-5 text-white" />
                                    </div>
                                    <h3 className="text-lg font-medium text-white">{t('settlement.settlements_list') || 'Settlements List'}</h3>
                                </div>
                                <span className="text-xs font-medium text-white bg-white/20 px-3 py-1 rounded-full">
                                    {records?.data?.length || 0} {t('settlement.records') || 'records'}
                                </span>
                            </div>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        {tableHeader?.map(item => (
                                            <th 
                                                key={item.column} 
                                                scope="col" 
                                                className="px-6 py-3.5 ltr:text-left rtl:text-right text-xs font-semibold text-gray-700 uppercase tracking-wider"
                                            >
                                                {!item.hidden && (
                                                    <>
                                                        {item.sortable ? (
                                                            <button 
                                                                onClick={() => handleChangeSort(item.column)} 
                                                                type="button" 
                                                                className="group inline-flex items-center"
                                                            >
                                                                {item.title}
                                                                <SortIcon sort={sort} field={item.column}/>
                                                            </button>
                                                        ) : (
                                                            <span>{item.title}</span>
                                                        )}
                                                    </>
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {records?.data?.map((record) => (
                                        <tr key={record?.settlementUuid} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">{record?.settlementUuid}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-500">{record?.supplier?.supplierName || 'N/A'}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-bold text-blue-600">{record?.requestAmount} {record?.currency?.symbol}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-bold text-green-600">{record?.settlementAmount} {record?.settlementAmount ? record?.currency?.symbol : 'N/A'}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-500">{record?.settlementDate}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <StatusBadge status={record?.status}/>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <Button 
                                                    onClick={() => {
                                                        window.location.href = `${process.env.REACT_APP_PAYD_API_BASE_URL}/${record?.receipt}`;
                                                    }} 
                                                    disabled={!record?.receipt}
                                                >
                                                    {t('settlement.download')}
                                                </Button>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <Button 
                                                    onClick={() => {
                                                        getSettlementReportUrlMutation.mutate({settlementUuid: record?.settlementUuid})
                                                    }}
                                                    disabled={getSettlementReportUrlMutation.isLoading}
                                                >
                                                    {t('settlement.download')}
                                                </Button>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="line-clamp-2 text-pretty text-sm text-gray-500">{record?.paymentReference}</div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <Link 
                                                    to={`/merchant/settlements/view/${record?.settlementUuid}`}
                                                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                                >
                                                    <EyeIcon className="h-4 w-4 ltr:mr-1 rtl:ml-1" />
                                                    {t('settlement.view')}
                                                </Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-200">
                            <PaginationFull paginator={records?.paginatorInfo} get={page} set={setPage}/>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
}

export default Settlement;
